'use client';

/**
 * Shared fullscreen chrome — the Esc-HOLD gesture, the top header reveal
 * model, the hold progress pill, and the first-run hint. Its one consumer
 * is the in-canvas takeover (NodeFrame portals the frame to <body>, the
 * phone's full screen). The standalone `/w` viewer (StandaloneWidget) used
 * it too until 2026-10-05, when its last page took the always-visible app
 * bar instead (components/widgets/app-address.ts says why); nothing on
 * `/w` reveals a header any more.
 *
 * Why a HOLD and not a plain Esc: widgets consume Esc locally (close a
 * palette, deselect, cancel a drag) — the EDA app alone binds it 19
 * times — so a tap must stay app-local. The SDK forwards Escape key
 * PHASES (down/up, never preventDefaulted) from inside widget iframes
 * (`sdk.v2.js`), WidgetBridge re-validates and re-emits them on
 * `workspace:host-key`, and this hook merges those with the host
 * document's own keydown/keyup. Only a ~700 ms hold reveals the header;
 * a quick tap while the header is up hides it again.
 *
 * Reveal channels:
 *   - hold Esc (desktop; taught by the first-run hint)
 *   - top-edge hover sliver (desktop, mouse only)
 *   - grab handle (touch — no Esc key, no hover; always visible)
 * Dismiss channels:
 *   - tap Esc
 *   - click into the widget (the SDK's `workspace:widget-focus` ping, or
 *     a host-DOM pointerdown inside the fullscreen container)
 *   - pointer leaves the header, when it was revealed by hover
 * Dialogs launched FROM the header portal to <body> — outside the
 * fullscreen container — so interacting with them never dismisses it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import type { RefObject, ReactNode } from 'react';
import { ChevronDown, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Hold duration before the header reveals. */
const HOLD_MS = 700;
/** Fraction of the hold before the progress pill appears (no flash on taps). */
const SHOW_AFTER = 150 / HOLD_MS;
/** A release faster than this while the header is up counts as a tap-hide. */
const TAP_MS = 300;
/** localStorage flag — "Don't show again", or auto-set on the first
 *  successful hold (the gesture is learned). */
const HINT_KEY = 'fsEscHintDismissed';

function hintDismissed(): boolean {
  try {
    return window.localStorage.getItem(HINT_KEY) === '1';
  } catch {
    // Storage blocked — never nag someone we can't remember.
    return true;
  }
}

export function markEscHintDone(): void {
  try {
    window.localStorage.setItem(HINT_KEY, '1');
  } catch {
    /* ignore */
  }
}

type RevealSource = 'esc' | 'hover' | 'handle';

export interface FullscreenChromeApi {
  active: boolean;
  headerShown: boolean;
  showHeader: (source?: RevealSource) => void;
  hideHeader: () => void;
  holding: boolean;
  /** 0..1 across the whole hold window. */
  progress: number;
  /** `(hover: none) and (pointer: coarse)` — phones/tablets. */
  isTouch: boolean;
  /**
   * Touch-only "immersive" mode: ALL chrome (header + grab handle) is
   * gone until the page reloads or fullscreen is re-entered. Desktop
   * never needs it — the reveal affordances there are already invisible.
   */
  immersive: boolean;
  enterImmersive: () => void;
  /** Attach to the outermost fullscreen container. Scopes the
   *  outside-click dismiss so <body>-portaled dialogs don't count. */
  containerRef: RefObject<HTMLDivElement | null>;
  headerRef: RefObject<HTMLDivElement | null>;
  onHeaderPointerEnter: () => void;
  onHeaderPointerLeave: () => void;
  onHeaderPointerDownCapture: () => void;
}

export function useFullscreenChrome({ active }: { active: boolean }): FullscreenChromeApi {
  const [headerShown, setHeaderShown] = useState(false);
  const [holding, setHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isTouch, setIsTouch] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const sourceRef = useRef<RevealSource>('esc');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const holdRef = useRef<{
    start: number;
    raf: number;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  const hoverHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownRef = useRef(false);
  shownRef.current = headerShown;

  useEffect(() => {
    try {
      setIsTouch(window.matchMedia('(hover: none) and (pointer: coarse)').matches);
    } catch {
      /* default: keyboard-capable */
    }
  }, []);

  const cancelHold = useCallback(() => {
    if (holdRef.current) {
      cancelAnimationFrame(holdRef.current.raf);
      if (holdRef.current.timer) clearTimeout(holdRef.current.timer);
      holdRef.current = null;
    }
    setHolding(false);
    setProgress(0);
  }, []);

  const showHeader = useCallback((source: RevealSource = 'esc') => {
    sourceRef.current = source;
    setHeaderShown(true);
  }, []);

  const hideHeader = useCallback(() => {
    setHeaderShown(false);
    if (hoverHideTimer.current) {
      clearTimeout(hoverHideTimer.current);
      hoverHideTimer.current = null;
    }
  }, []);

  // Esc hold state machine. Two event sources feed it, but never both at
  // once — a keydown lands either in the host document OR inside a widget
  // iframe (relayed) — so `begin` still guards against double-starts.
  // We never preventDefault: Esc stays a widget/content-local key; the
  // chrome only OBSERVES it.
  useEffect(() => {
    if (!active) return;
    const begin = () => {
      if (holdRef.current) return;
      if (shownRef.current) {
        // Header already up: just time the press so release can
        // distinguish a tap-hide from an idle hold. No progress UI.
        holdRef.current = { start: performance.now(), raf: 0, timer: null };
        return;
      }
      const start = performance.now();
      // COMPLETION rides a plain timeout, not the rAF loop — rAF freezes
      // whenever the window counts as hidden (occluded window, background
      // tab), and the hold must complete wherever key events still flow.
      // rAF only animates the cosmetic progress ring.
      const timer = setTimeout(() => {
        if (!holdRef.current) return;
        cancelAnimationFrame(holdRef.current.raf);
        holdRef.current = null;
        setHolding(false);
        setProgress(0);
        sourceRef.current = 'esc';
        setHeaderShown(true);
        markEscHintDone();
      }, HOLD_MS);
      const tick = () => {
        if (!holdRef.current) return;
        setProgress(Math.min(1, (performance.now() - start) / HOLD_MS));
        holdRef.current.raf = requestAnimationFrame(tick);
      };
      holdRef.current = { start, raf: requestAnimationFrame(tick), timer };
      setHolding(true);
    };
    const end = () => {
      const held = holdRef.current ? performance.now() - holdRef.current.start : null;
      cancelHold();
      if (held !== null && held < TAP_MS && shownRef.current) hideHeader();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.repeat) return;
      begin();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') end();
    };
    const onHostKey = (e: Event) => {
      const d = (e as CustomEvent<{ key?: string; phase?: string }>).detail;
      if (d?.key !== 'Escape') return;
      if (d.phase === 'down') begin();
      else if (d.phase === 'up') end();
    };
    // Focus leaving mid-press (Cmd-Tab, dialog steal) would strand a
    // phantom hold — treat blur as a release with no tap semantics.
    const onBlur = () => cancelHold();
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('workspace:host-key', onHostKey);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('workspace:host-key', onHostKey);
      window.removeEventListener('blur', onBlur);
      cancelHold();
    };
  }, [active, cancelHold, hideHeader]);

  // Dismiss-on-interaction while the header is up: a click into the
  // widget iframe (SDK focus ping) or a host-DOM pointerdown inside the
  // fullscreen container but outside the header. Anything OUTSIDE the
  // container (Remix/Drive/Share dialogs portal to <body>) is ignored.
  useEffect(() => {
    if (!active || !headerShown) return;
    const onWidgetFocus = () => hideHeader();
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      const container = containerRef.current;
      const header = headerRef.current;
      if (!t || !container || !container.contains(t)) return;
      if (header && header.contains(t)) return;
      hideHeader();
    };
    window.addEventListener('workspace:widget-focus', onWidgetFocus);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('workspace:widget-focus', onWidgetFocus);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [active, headerShown, hideHeader]);

  // Leaving fullscreen resets the whole gesture surface (immersive
  // included — re-entering fullscreen brings the chrome back).
  useEffect(() => {
    if (!active) {
      hideHeader();
      cancelHold();
      setImmersive(false);
    }
  }, [active, cancelHold, hideHeader]);

  const enterImmersive = useCallback(() => {
    setImmersive(true);
    hideHeader();
  }, [hideHeader]);

  // A hover-revealed header retracts shortly after the pointer leaves it;
  // an Esc/handle reveal stays until explicitly dismissed. Any real
  // interaction with the header promotes it to sticky so an open menu
  // doesn't vanish mid-click.
  const onHeaderPointerEnter = useCallback(() => {
    if (hoverHideTimer.current) {
      clearTimeout(hoverHideTimer.current);
      hoverHideTimer.current = null;
    }
  }, []);
  const onHeaderPointerLeave = useCallback(() => {
    if (sourceRef.current !== 'hover' || !shownRef.current) return;
    if (hoverHideTimer.current) clearTimeout(hoverHideTimer.current);
    hoverHideTimer.current = setTimeout(() => {
      hoverHideTimer.current = null;
      setHeaderShown(false);
    }, 300);
  }, []);
  const onHeaderPointerDownCapture = useCallback(() => {
    sourceRef.current = 'esc';
  }, []);

  return {
    active,
    headerShown,
    showHeader,
    hideHeader,
    holding,
    progress,
    isTouch,
    immersive,
    enterImmersive,
    containerRef,
    headerRef,
    onHeaderPointerEnter,
    onHeaderPointerLeave,
    onHeaderPointerDownCapture,
  };
}

/**
 * Renders the reveal affordances + the sliding header bar. Mount INSIDE
 * the fullscreen container (it positions absolutely against it); pass
 * the surface's header content as children.
 */
export function FullscreenChrome({
  chrome,
  children,
  testId,
  grabHandle = true,
}: {
  chrome: FullscreenChromeApi;
  children: ReactNode;
  testId?: string;
  /**
   * Draw the touch grab handle. Off for a signed-out viewer of an apex `/w/`
   * page (round 7, lane 4, item 4; see StandaloneHeader): there the handle
   * sat on the app's own top bar, between the app's name and its "Sign in".
   */
  grabHandle?: boolean;
}) {
  const t = useT();
  // Immersive (touch): every affordance gone until reload / fullscreen
  // re-entry — games get the whole screen back.
  if (!chrome.active || chrome.immersive) return null;
  return (
    <>
      {/* Desktop: invisible top-edge sliver — pushing the mouse to the top
          of the screen reveals the header, mirroring the retired rail's
          left-edge sliver. Mouse only; touch gets the grab handle. */}
      {!chrome.headerShown && !chrome.isTouch && (
        <div
          aria-hidden
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') chrome.showHeader('hover');
          }}
          className="absolute inset-x-0 top-0 z-[65] h-2.5"
        />
      )}
      {grabHandle && chrome.isTouch && !chrome.headerShown && (
        <GrabHandle onReveal={() => chrome.showHeader('handle')} />
      )}
      <div
        ref={chrome.headerRef}
        data-testid={testId}
        onPointerEnter={chrome.onHeaderPointerEnter}
        onPointerLeave={chrome.onHeaderPointerLeave}
        onPointerDownCapture={chrome.onHeaderPointerDownCapture}
        className={cn(
          'absolute inset-x-0 top-0 z-[60] transition-transform duration-200 ease-out',
          chrome.headerShown
            ? 'translate-y-0'
            : 'pointer-events-none -translate-y-[110%]',
        )}
      >
        {children}
        {/* Touch-only immersive escape: no Esc key means the grab handle
            is otherwise permanent — this hides ALL chrome until reload
            (or fullscreen re-entry). Rides inside the sliding container
            so it animates with the bar, pinned under its right edge. */}
        {chrome.isTouch && chrome.headerShown && (
          <button
            type="button"
            onClick={chrome.enterImmersive}
            data-testid="fullscreen-immersive"
            className="liquid-glass ws-panel-solid absolute right-3 top-full mt-2 inline-flex h-7 items-center gap-1.5 rounded-full border border-border/40 px-2.5 text-[11px] font-medium text-foreground/75 shadow-md"
          >
            <EyeOff className="h-3.5 w-3.5" />
            {t('workspace.fullscreen.hideControls')}
          </button>
        )}
      </div>
      <EscHoldProgress chrome={chrome} />
      <EscHint chrome={chrome} />
    </>
  );
}

/**
 * Touch affordance: a small always-visible handle at the top-center.
 * Tap it or drag it downward to reveal the header. Host-rendered (above
 * the iframe) because touches inside the iframe never reach the host,
 * and a bare edge swipe would collide with OS edge gestures. Sits below
 * the safe-area inset so it never hides under a notch.
 */
function GrabHandle({ onReveal }: { onReveal: () => void }) {
  const t = useT();
  const startY = useRef<number | null>(null);
  return (
    <button
      type="button"
      aria-label={t('workspace.fullscreen.showControls')}
      data-testid="fullscreen-grab-handle"
      onClick={onReveal}
      onPointerDown={(e) => {
        startY.current = e.clientY;
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (startY.current !== null && e.clientY - startY.current > 24) {
          startY.current = null;
          onReveal();
        }
      }}
      onPointerUp={() => {
        startY.current = null;
      }}
      onPointerCancel={() => {
        startY.current = null;
      }}
      className="liquid-glass absolute left-1/2 z-[65] flex h-6 w-12 -translate-x-1/2 items-center justify-center rounded-b-xl border border-t-0 border-border/40 text-foreground/70 shadow-md"
      style={{ top: 'env(safe-area-inset-top, 0px)', touchAction: 'none' }}
    >
      <ChevronDown className="h-4 w-4" />
    </button>
  );
}

/** Radial progress pill shown mid-hold (Keep holding Esc). */
function EscHoldProgress({ chrome }: { chrome: FullscreenChromeApi }) {
  const t = useT();
  if (!chrome.holding || chrome.headerShown || chrome.progress < SHOW_AFTER) {
    return null;
  }
  const R = 7;
  const C = 2 * Math.PI * R;
  // Normalize so the ring starts empty the moment the pill appears.
  const p = Math.min(1, (chrome.progress - SHOW_AFTER) / (1 - SHOW_AFTER));
  return (
    <div
      className="pointer-events-none absolute left-1/2 top-4 z-[70] -translate-x-1/2"
      data-testid="fullscreen-esc-progress"
    >
      <div className="liquid-glass ws-panel-solid flex items-center gap-2 rounded-full border border-border/40 px-3.5 py-1.5 shadow-xl">
        <svg viewBox="0 0 18 18" className="h-4 w-4 -rotate-90" aria-hidden>
          <circle
            cx="9"
            cy="9"
            r={R}
            fill="none"
            strokeWidth="2.5"
            className="stroke-border"
          />
          <circle
            cx="9"
            cy="9"
            r={R}
            fill="none"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - p)}
            className="stroke-primary"
          />
        </svg>
        <span className="whitespace-nowrap text-[12px] font-medium text-foreground/85">
          {t('workspace.fullscreen.keepHoldingEsc')}
        </span>
      </div>
    </div>
  );
}

/**
 * First-run hint shown on fullscreen entry: teaches the hold gesture,
 * fades after 5 s, returns on the next entry until "Don't show again"
 * (or the first successful hold) sets the storage flag. Keyboard-capable
 * devices only — on touch the grab handle is its own affordance.
 */
function EscHint({ chrome }: { chrome: FullscreenChromeApi }) {
  const t = useT();
  // Assume dismissed until storage is read so there is no SSR flash.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!chrome.active || chrome.isTouch) return;
    if (hintDismissed()) return;
    setVisible(true);
    const tid = setTimeout(() => setVisible(false), 5000);
    return () => {
      clearTimeout(tid);
      setVisible(false);
    };
  }, [chrome.active, chrome.isTouch]);
  // The moment the header reveals, the lesson is over.
  useEffect(() => {
    if (chrome.headerShown) setVisible(false);
  }, [chrome.headerShown]);
  if (!visible || chrome.headerShown || chrome.holding) return null;
  return (
    <div
      className="absolute left-1/2 top-4 z-[69] -translate-x-1/2"
      data-testid="fullscreen-esc-hint"
    >
      <div className="liquid-glass ws-panel-solid flex items-center gap-2.5 rounded-full border border-border/40 py-1.5 pl-3.5 pr-1.5 shadow-xl">
        <span className="whitespace-nowrap text-[12px] text-foreground/85">
          Hold{' '}
          <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[10px] font-semibold">
            Esc
          </kbd>{' '}
          to show app controls
        </span>
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="rounded-full px-2 py-0.5 text-[11px] font-medium text-foreground/70 transition-colors hover:bg-accent/60 hover:text-foreground"
        >
          {t('workspace.fullscreen.gotIt')}
        </button>
        <button
          type="button"
          onClick={() => {
            markEscHintDone();
            setVisible(false);
          }}
          data-testid="fullscreen-esc-hint-never"
          className="whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium text-foreground/70 transition-colors hover:bg-accent/60 hover:text-foreground"
        >
          Don&apos;t show again
        </button>
      </div>
    </div>
  );
}
